import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSocket } from "./SocketContext";
import { setConversations } from "../redux/conversationSlice";
import { setMessages } from "../redux/messageSlice";
import { setMessageIds } from "../redux/messageRead";
import { setliveUsers } from "../redux/liveUserSlice";

const ConversationUpdatesContext = createContext();

export const useConversationUpdates = () => useContext(ConversationUpdatesContext);

export const ConversationUpdatesProvider = ({ children }) => {
  const { socket } = useSocket();
  const dispatch = useDispatch();
  const profileId = useSelector((state) => state.auth.profile?._id);
  const conversations = useSelector((state) => state.conversation.conversations);
  const messages = useSelector((state) => state.message.data);
  const [latestMessages, setLatestMessages] = useState([]);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlertMessage, setShowAlertMessage] = useState(false);

  const updateConversationData = (existData, newData) => {
    const safeExisting = Object.isExtensible(existData) ? existData : [...existData];
    const updatedConversations = safeExisting.map((conversation) => {
      if (conversation.conversationId === newData.conversationId) {
        return {
          ...conversation,
          data: [
            ...conversation.data,
            ...newData.data.filter(
              (newMsg) =>
                !conversation.data.some(
                  (existingMsg) => existingMsg.id === newMsg.id
                )
            ),
          ],
        };
      }
      return conversation;
    });
    const conversationExists = updatedConversations.some(
      (conv) => conv.conversationId === newData.conversationId
    );
    if (!conversationExists) {
      updatedConversations.push({ ...newData });
    }
    return updatedConversations;
  };

  useEffect(() => {
    if (!socket) return undefined;

    const handleGetLiveUser = (connectedUser) => {
      dispatch(setliveUsers({ liveUsers: connectedUser }));
    };

    const handleSendConversationsUser = (data) => {
      dispatch(setConversations({ conversations: data }));
    };

    const handleSetSideBarUsers = (data) => {
      if (data?.data) {
        dispatch(setConversations({ conversations: data.data }));
      }
    };

    const handleLastestMessage = (data) => {
      if (!Array.isArray(data)) return;
      setLatestMessages(data);

      if (Array.isArray(conversations) && conversations.length > 0) {
        const updatedConversations = conversations.map((conversation) => {
          const latest = data.find(
            (item) => item.conversationId === conversation.conversationId
          );
          if (!latest) return conversation;
          return {
            ...conversation,
            latestMessage:
              latest.latestMessage ?? conversation.latestMessage,
            unreadCount:
              typeof latest.unreadCount === "number"
                ? latest.unreadCount
                : conversation.unreadCount,
          };
        });
        dispatch(setConversations({ conversations: updatedConversations }));
      }

      const threadId = localStorage.getItem("thread_id_") || false;
      const filterData = data.filter((messageThread) => {
        const isUnread =
          messageThread.notifiy_seen === false &&
          messageThread.receiverId_ === profileId;
        if (!threadId) return isUnread;
        return isUnread && messageThread.conversationId !== threadId;
      });

      if (filterData.length > 0) {
        const messageIds = filterData.map((item) => item.id).filter(Boolean);
        const first = filterData[0];
        const userName = first?.user?.username || "User";
        setAlertMessage(
          `${userName.charAt(0).toUpperCase() + userName.slice(1)} send the message`
        );
        setShowAlertMessage(true);
        setTimeout(() => {
          setShowAlertMessage(false);
          setAlertMessage("");
        }, 1000);
        if (messageIds.length > 0) {
          socket.emit("messageNotification", { messageIds });
        }
      }
    };

    const handleGetMessage = (data) => {
      const updatedData = updateConversationData(messages, data);
      dispatch(setMessages(updatedData));
    };

    const handleMessagesRead = (data) => {
      dispatch(setMessageIds(data));
    };

    socket.on("getLiveUser", handleGetLiveUser);
    socket.on("sendConverstionsUser", handleSendConversationsUser);
    socket.on("setSideBarUsers", handleSetSideBarUsers);
    socket.on("lastestMessage", handleLastestMessage);
    socket.on("getMessage", handleGetMessage);
    socket.on("messagesRead", handleMessagesRead);

    return () => {
      socket.off("getLiveUser", handleGetLiveUser);
      socket.off("sendConverstionsUser", handleSendConversationsUser);
      socket.off("setSideBarUsers", handleSetSideBarUsers);
      socket.off("lastestMessage", handleLastestMessage);
      socket.off("getMessage", handleGetMessage);
      socket.off("messagesRead", handleMessagesRead);
    };
  }, [socket, dispatch, conversations, messages, profileId]);

  const closeAlert = () => setShowAlertMessage(false);

  const value = useMemo(
    () => ({
      latestMessages,
      alertMessage,
      showAlertMessage,
      closeAlert,
    }),
    [latestMessages, alertMessage, showAlertMessage]
  );

  return (
    <ConversationUpdatesContext.Provider value={value}>
      {children}
    </ConversationUpdatesContext.Provider>
  );
};
